import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';
import { setAuthorizationToken } from '../utils';
import { Field } from '../forms/finalForm';
import renderField from '../forms/render/renderField';


const SUGGESTION_DELAY_MS = 500;

const InputSuggestion = ({
  name,
  value = '',
  getSuggestions,
  label,
  classNames = '',
  placeholder,
  requestWithId = false,
  errorClassNames = '',
  idForRequest = null,
  shouldSaveChoiceId = false,
  choiceIdName = '',
  callRequestWhenFocus = false,
  // Field description type ("inputWithSuggestion") must not reach the input.
  type: _type,
  ...rest
}) => {
  const [suggestionSkills, setSuggestionSkills] = useState([]);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const delayTimer = useRef(null);
  const form = useForm();

  const onBlur = () => {
    setTimeout(() => {
      setSuggestionOpen(false);
    }, 150);
  };

  const onChoiceSuggestion = (choice) => {
    form.change(name, choice);
    if (shouldSaveChoiceId) {
      const suggestion = suggestionSkills.find(({ text }) => text === choice);
      if (suggestion) {
        form.change(choiceIdName, suggestion.id);
      }
    }
  };

  const getSuggestionsOptions = currentValue => new Promise((resolve) => {
    clearTimeout(delayTimer.current);
    delayTimer.current = setTimeout(() => {
      const valueForRequest = requestWithId ? idForRequest : currentValue;
      resolve(getSuggestions(valueForRequest)
        .then(({ data }) => {
          const { items = null } = data;
          setSuggestionSkills(items || data);
        })
        .catch(() => setSuggestionSkills([]))
        .finally(() => setAuthorizationToken(localStorage.getItem('token'))));
    }, SUGGESTION_DELAY_MS);
  });

  useEffect(() => {
    if (idForRequest && requestWithId) {
      getSuggestionsOptions(idForRequest);
    }
    // Reload suggestions only when the selected institution changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idForRequest]);

  useEffect(() => () => clearTimeout(delayTimer.current), []);

  const onInputChange = ({ currentTarget: { value: inputValue } }) => {
    if (inputValue.length >= 2 || requestWithId) {
      getSuggestionsOptions(inputValue);
    }
  };

  const onFocus = () => {
    setSuggestionOpen(true);
    if ((value && value.length) > 2 && callRequestWhenFocus) {
      getSuggestionsOptions(value);
    }
  };

  const modItems = suggestionSkills.map(({ text = null, name: itemName = null }) => (
    { value: text || itemName, label: text || itemName }
  ));

  return (
    <>
      <Field
        name={name}
        component={renderField}
        label={label}
        onChange={onInputChange}
        {...rest}
        onFocus={onFocus}
        onBlur={onBlur}
        className={`suggestion-input ${classNames} ${suggestionOpen && 'suggestion-input-focus-border'}`}
        errorClassNames={errorClassNames}
        placeholder={placeholder}
        autoComplete="off"
      />
      {suggestionOpen && (
        <div className="suggestion-list-wrapper">
          <ul className="suggestion-list">
            {modItems.length
              ? modItems.map(({ value: itemValue }, key) => (
                <li
                  key={String(key)}
                  onClick={() => {
                    onChoiceSuggestion(itemValue);
                  }}
                >
                  {itemValue}
                </li>
              ))
              : <li>ничего не найдено</li>}
          </ul>
        </div>
      )}
    </>
  );
};

InputSuggestion.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  label: PropTypes.string.isRequired,
  getSuggestions: PropTypes.func.isRequired,
  classNames: PropTypes.string,
  errorClassNames: PropTypes.string,
  callRequestWhenFocus: PropTypes.bool,
  shouldSaveChoiceId: PropTypes.bool,
  choiceIdName: PropTypes.string,
  idForRequest: PropTypes.number,
  requestWithId: PropTypes.bool,
};

export default InputSuggestion;
